import { api } from '../../../../lib/api';
import { normalizeAppLanguage } from '../../../../assets/utils/comm/languageConstants';

/** 사이트 언어에 맞춰 FMS 회원 유형 표시명 (en이면 displayNmEn, 그 외 displayNm) */
export function memberTypeDisplayName(def, lang) {
  if (!def) return '';
  if (normalizeAppLanguage(lang) === 'en') {
    const en = String(def.displayNmEn || '').trim();
    if (en) return en;
  }
  return String(def.displayNm || def.objectNm || def.objectNmEn || '').trim();
}

/** 디렉터리 상세 검색 필드 라벨 — 영어면 labelEn(FMS attributeLabelEn), 없으면 한글 label */
export function searchFieldDisplayLabel(field, lang) {
  if (!field) return '';
  if (normalizeAppLanguage(lang) === 'en') {
    const en = String(field.labelEn || '').trim();
    if (en) return en;
  }
  return String(field.label || field.key || '').trim();
}

/** 검색 칩·memberKind 매핑 — expertSearchMapping에서 재export (순환 의존 방지: API 모듈이 mapping을 먼저 로드하지 않음) */
export const MEMBER_KIND_CHIP = {
  ALL: '전체',
  INDIVIDUAL: '전문가(개인)',
  ORG: '기관·기업',
};

/**
 * API 쿼리용 회원 구분 (레거시 칩 라벨 → 백엔드 memberKind)
 */
export function memberKindLabelToApi(label) {
  if (label == null || label === '' || label === MEMBER_KIND_CHIP.ALL) return '';
  const u = String(label).trim().toUpperCase();
  if (u === 'INDIVIDUAL' || u === 'ORG') return u;
  if (label === MEMBER_KIND_CHIP.INDIVIDUAL) return 'INDIVIDUAL';
  if (label === MEMBER_KIND_CHIP.ORG) return 'ORG';
  return '';
}

/**
 * 회원가입(Register.jsx) 제출 시와 동일 — CETUS_USER.AUTHOR_CD에 넣는 값
 * @param {{ objectNmEn?: string, displayNmEn?: string }} def
 */
export function authorCdFromMemberTypeDefinition(def) {
  if (!def) return '';
  const selectedMemberType = def.objectNmEn || '';
  const selectedMember = def;
  return selectedMember?.displayNmEn
    ? String(selectedMember.displayNmEn).trim().toUpperCase()
    : String(selectedMemberType).trim().toUpperCase();
}

/**
 * FMS 카테고리별 회원 유형 (individualUserInfo / organizationUserInfo 각각 sortNo 정렬)
 * @returns {Promise<{ individual: Array<object>, organization: Array<object> }>}
 */
let signupDefinitionsBucketsPromise = null;

function sortMemberTypeDefinitions(arr) {
  return Array.isArray(arr) ? [...arr].sort((a, b) => (a.sortNo ?? 999) - (b.sortNo ?? 999)) : [];
}

async function loadSignupMemberTypeDefinitionsBuckets() {
  const [individualRes, orgRes] = await Promise.all([
    api.get('/api/objects/definitions/category/individualUserInfo'),
    api.get('/api/objects/definitions/category/organizationUserInfo'),
  ]);
  return {
    individual: sortMemberTypeDefinitions(Array.isArray(individualRes) ? individualRes : []),
    organization: sortMemberTypeDefinitions(Array.isArray(orgRes) ? orgRes : []),
  };
}

/**
 * 카테고리별로 나눈 회원 유형 (검색 폼 등 UI 구역 분리용). 동시 호출은 한 번의 API 페어만 사용.
 */
export async function fetchSignupMemberTypeDefinitionsGrouped() {
  if (!signupDefinitionsBucketsPromise) {
    signupDefinitionsBucketsPromise = loadSignupMemberTypeDefinitionsBuckets().catch((e) => {
      signupDefinitionsBucketsPromise = null;
      throw e;
    });
  }
  return signupDefinitionsBucketsPromise;
}

/**
 * 회원가입(Register.jsx useEffect fetchMemberTypes)과 동일 — FMS 카테고리로 회원 유형 목록 (전체 sortNo 병합 정렬)
 * @returns {Promise<Array<{ uid, objectNmEn, displayNm, displayNmEn, sortNo, ... }>>}
 */
export async function fetchSignupMemberTypeDefinitions() {
  const { individual, organization } = await fetchSignupMemberTypeDefinitionsGrouped();
  const combinedRes = [...individual, ...organization];
  if (combinedRes.length === 0) return [];
  return combinedRes.sort((a, b) => (a.sortNo ?? 999) - (b.sortNo ?? 999));
}

/** 디렉터리 search_data와 겹치는 기관 마스터 — 회원 유형 정의에 없어도 속성 라벨 수집 */
const EXTRA_OBJECT_KEYS_FOR_DIRECTORY_LABELS = [
  'company_org_info',
  'government_org_info',
  'institution_org_info',
];

/**
 * FMS attributes 한 건 → 표시 라벨 (en이면 attributeLabelEn, 그 외 한글 attributeNm)
 * @param {object} attr
 * @param {string} [lang]
 */
export function labelFromFmsAttribute(attr, lang) {
  if (!attr || typeof attr !== 'object') return '';
  if (normalizeAppLanguage(lang) === 'en') {
    const en = String(attr.attributeLabelEn || attr.displayNmEn || '').trim();
    if (en) return en;
  }
  const s =
    attr.displayNm ||
    attr.attributeNm ||
    attr.attributeLabel ||
    attr.label ||
    attr.attributeNmKo ||
    '';
  return String(s).trim();
}

/**
 * GET /api/objects/{objectKey}/attributes 응답 배열 → attributeNmEn → 라벨 맵 (대소문·스네이크 변형 키 동시 등록)
 * @param {Array<object>} attrs
 * @returns {Record<string, string>}
 */
export function buildFieldLabelMapFromAttributes(attrs, lang) {
  const map = Object.create(null);
  if (!Array.isArray(attrs)) return map;
  for (const attr of attrs) {
    const key = attr.attributeNmEn || attr.attributeNm || attr.attribute_nm_en;
    if (!key) continue;
    const label = labelFromFmsAttribute(attr, lang);
    if (!label) continue;
    const k = String(key).trim();
    map[k] = label;
    map[k.toLowerCase()] = label;
    const snake = k
      .replace(/([A-Z])/g, '_$1')
      .replace(/^_/, '')
      .toLowerCase();
    if (snake && snake !== k.toLowerCase()) map[snake] = label;
  }
  return map;
}

let cachedFmsFieldLabelMaps = { ko: null, en: null };
let fmsFieldLabelMapPromise = null;
let memberDirectorySearchFieldsCache = null;
let memberDirectorySearchFieldsInFlight = null;

/** 동일 objectKey에 대한 /attributes 요청 중복 방지(병렬 배치·재진입 시 같은 URL N번 나가는 것 방지) */
const fmsAttributesInFlight = new Map();
const fmsAttributesResultCache = new Map();

function registerFieldKeyAliases(map, key, label) {
  if (!map || key == null || key === '' || label == null || label === '') return;
  const k = String(key).trim();
  const L = String(label).trim();
  if (!k || !L) return;
  map[k] = L;
  map[k.toLowerCase()] = L;
  const snake = k
    .replace(/([A-Z])/g, '_$1')
    .replace(/^_/, '')
    .toLowerCase();
  if (snake && snake !== k.toLowerCase()) map[snake] = L;
}

function buildFieldLabelMapFromSearchFieldMetadata(meta, lang) {
  const map = Object.create(null);
  const entries = [
    ...(Array.isArray(meta?.individual) ? meta.individual : []),
    ...(Array.isArray(meta?.organization) ? meta.organization : []),
  ];
  for (const row of entries) {
    const key = row?.key;
    const label = searchFieldDisplayLabel(row, lang);
    registerFieldKeyAliases(map, key, label);
  }
  return map;
}

async function fetchFmsObjectAttributesOnce(objectKey) {
  const key = String(objectKey || '').trim();
  if (!key) return null;
  const hit = fmsAttributesResultCache.get(key);
  if (hit != null) return hit;
  let inflight = fmsAttributesInFlight.get(key);
  if (!inflight) {
    inflight = api
      .get(`/api/objects/${encodeURIComponent(key)}/attributes`)
      .then((attrs) => {
        fmsAttributesResultCache.set(key, attrs);
        fmsAttributesInFlight.delete(key);
        return attrs;
      })
      .catch((e) => {
        fmsAttributesInFlight.delete(key);
        throw e;
      });
    fmsAttributesInFlight.set(key, inflight);
  }
  return inflight;
}

/**
 * 회원 유형별 오브젝트 + 기관 마스터에 대해 attributes를 병렬 조회해 필드키→표시명 맵 생성.
 * 브라우저 세션 동안 한 번 성공하면 언어별 메모리 캐시 재사용 (탭 이동·상세 재진입 부담 감소).
 *
 * 호출 수: `definitions`를 넘기면 카테고리 정의 API는 생략 가능.
 * 그렇지 않으면 individualUserInfo + organizationUserInfo 2회 + 고유 objectNmEn당 attributes 1회(통상 ~10±).
 * @param {{ forceRefresh?: boolean, definitions?: Array<{ objectNmEn?: string }>, lang?: string }} [opts]
 * @returns {Promise<Record<string, string>>}
 */
export async function fetchMergedFmsFieldLabelMap(opts = {}) {
  const { forceRefresh = false, definitions: definitionsOverride, lang } = opts;
  const locale = normalizeAppLanguage(lang) === 'en' ? 'en' : 'ko';
  if (!forceRefresh && cachedFmsFieldLabelMaps[locale]) return cachedFmsFieldLabelMaps[locale];
  if (fmsFieldLabelMapPromise) {
    await fmsFieldLabelMapPromise;
    if (!forceRefresh && cachedFmsFieldLabelMaps[locale]) return cachedFmsFieldLabelMaps[locale];
  }

  fmsFieldLabelMapPromise = (async () => {
    try {
      /**
       * 우선: 백엔드 배치 메타 API 1회(/api/public/member-directory/search-data-fields)
       * - 기존 object별 /attributes 다건 호출보다 요청 수를 크게 줄임.
       */
      try {
        const searchMeta = await fetchMemberDirectorySearchFields();
        const koMap = buildFieldLabelMapFromSearchFieldMetadata(searchMeta, 'ko');
        const enMap = buildFieldLabelMapFromSearchFieldMetadata(searchMeta, 'en');
        if (Object.keys(koMap).length > 0 || Object.keys(enMap).length > 0) {
          cachedFmsFieldLabelMaps.ko = koMap;
          cachedFmsFieldLabelMaps.en = enMap;
          return;
        }
      } catch (_) {
        /* fallback below */
      }

      const definitions =
        definitionsOverride != null ? definitionsOverride : await fetchSignupMemberTypeDefinitions();
      const keys = new Set(EXTRA_OBJECT_KEYS_FOR_DIRECTORY_LABELS);
      for (const d of definitions || []) {
        if (d?.objectNmEn) keys.add(String(d.objectNmEn).trim());
      }

      const mergedKo = Object.create(null);
      const mergedEn = Object.create(null);
      await Promise.all(
        [...keys].map(async (objectKey) => {
          try {
            const attrs = await fetchFmsObjectAttributesOnce(objectKey);
            const list = Array.isArray(attrs) ? attrs : [];
            Object.assign(mergedKo, buildFieldLabelMapFromAttributes(list, 'ko'));
            Object.assign(mergedEn, buildFieldLabelMapFromAttributes(list, 'en'));
          } catch (_) {
            /* 해당 오브젝트 없음·FMS 일시 오류는 건너뜀 */
          }
        })
      );
      cachedFmsFieldLabelMaps.ko = mergedKo;
      cachedFmsFieldLabelMaps.en = mergedEn;
    } finally {
      fmsFieldLabelMapPromise = null;
    }
  })();
  await fmsFieldLabelMapPromise;
  return cachedFmsFieldLabelMaps[locale] || Object.create(null);
}

export function clearFmsFieldLabelMapCache() {
  cachedFmsFieldLabelMaps = { ko: null, en: null };
  fmsFieldLabelMapPromise = null;
  memberDirectorySearchFieldsCache = null;
  memberDirectorySearchFieldsInFlight = null;
  fmsAttributesInFlight.clear();
  fmsAttributesResultCache.clear();
}

/**
 * CETUS_USER.AUTHOR_CD(대문자) → FMS 회원 유형 표시명 (검색 필터·가입과 동일 정의 목록 기준)
 * 동일 authorCd가 여러 정의에 있으면 sortNo 순 첫 항목만 사용
 * @param {Array<{ objectNmEn?: string, displayNmEn?: string, displayNm?: string, objectNm?: string }>} definitions
 * @returns {Record<string, string>}
 */
export function buildAuthorCdToFmsDisplayMap(definitions, lang) {
  const map = Object.create(null);
  if (!Array.isArray(definitions)) return map;
  for (const def of definitions) {
    const cd = authorCdFromMemberTypeDefinition(def);
    if (!cd || map[cd] !== undefined) continue;
    map[cd] = memberTypeDisplayName(def, lang) || cd;
  }
  return map;
}

/**
 * 목록 검색 조건 — 빈 문자열·전체 구분 제외 후 객체 생성
 * 회원 유형 칩: FMS 카테고리(개인/기업·기관)에 맞게 memberKind(INDIVIDUAL|ORG) + authorCd 동시 전달.
 * 상세: searchData — Spring 바인딩용 searchData[key]=value
 */
export function buildMemberDirectoryCriteria(raw = {}) {
  const out = {};
  const kw = raw.keyword;
  if (kw != null && String(kw).trim() !== '') {
    out.keyword = String(kw).trim();
  }
  const sd = raw.searchData;
  if (sd && typeof sd === 'object' && !Array.isArray(sd)) {
    for (const [k, v] of Object.entries(sd)) {
      if (v != null && String(v).trim() !== '') {
        out[`searchData[${k}]`] = String(v).trim();
      }
    }
  }
  const ac = raw.authorCd != null ? String(raw.authorCd).trim() : '';
  if (ac) {
    out.authorCd = ac;
  }
  const mk = memberKindLabelToApi(raw.memberKind);
  if (mk) {
    out.memberKind = mk;
  }
  return out;
}

/**
 * 디렉터리 상세 검색 필드 메타 (search_data 키 + 라벨)
 * @returns {Promise<{ individual: Array<{ key: string, label: string, labelEn?: string }>, organization: Array<{ key: string, label: string, labelEn?: string }> }>}
 */
export async function fetchMemberDirectorySearchFields() {
  if (memberDirectorySearchFieldsCache) {
    return memberDirectorySearchFieldsCache;
  }
  if (memberDirectorySearchFieldsInFlight) {
    return memberDirectorySearchFieldsInFlight;
  }
  memberDirectorySearchFieldsInFlight = api
    .get('/api/public/member-directory/search-data-fields')
    .then((res) => {
      const normalized = res ?? { individual: [], organization: [] };
      memberDirectorySearchFieldsCache = normalized;
      memberDirectorySearchFieldsInFlight = null;
      return normalized;
    })
    .catch((e) => {
      memberDirectorySearchFieldsInFlight = null;
      throw e;
    });
  return memberDirectorySearchFieldsInFlight;
}

/**
 * 공개 회원 디렉터리 API (/api/public/member-directory)
 * @param {object} params - page(1-based), size, keyword, authorCd, memberKind, searchData(객체 → searchData[key] 쿼리)
 * @returns {Promise<{ list: Array, totalElements: number, totalPages: number, number: number, size: number }>}
 */
export async function fetchMemberDirectoryUsers(params = {}) {
  const { page = 1, size = 10, ...rest } = params;
  const query = { pageNumber: page, size, ...buildMemberDirectoryCriteria(rest) };
  const res = await api.get('/api/public/member-directory/users', { params: query });
  const list = res?.list || res?.content || [];
  const totalElements = res?.totalElements ?? list.length;
  const pageSize = res?.size ?? size;
  return {
    list,
    totalElements,
    totalPages: res?.totalPages ?? Math.max(1, Math.ceil(totalElements / (pageSize || 1))),
    number: res?.number ?? page,
    size: pageSize,
  };
}

export async function fetchMemberDirectoryAuthorTypes() {
  const res = await api.get('/api/public/member-directory/author-types');
  return Array.isArray(res) ? res : res?.data || [];
}

/**
 * @param {number|string} uid - CETUS 사용자 uid 또는 기관(ob_company_org_info) uid
 * @param {{ kind?: 'org' }} [opts] - kind=org 이면 기관 디렉터리 행(OO.uid)으로 조회
 */
export async function fetchPublicMemberProfile(uid, opts = {}) {
  const params = {};
  if (opts.kind === 'org') {
    params.kind = 'org';
  }
  const res = await api.get(`/api/public/member-directory/users/${uid}`, { params });
  return res;
}
